// ─── Auth Context ─────────────────────────────────────────────────────────────
// Single Supabase auth subscription shared across the entire app.
// Provides user state via useAuth() hook — components never subscribe directly.

import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from "react";
import {
  getSession,
  onAuthStateChange,
  type User,
} from "../services/authService";

// ─── Context Shape ──────────────────────────────────────────────────────

interface AuthContextValue {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let receivedAuthEvent = false;
    getSession().then((session) => {
      if (cancelled || receivedAuthEvent) return;
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(() => { if (!cancelled && !receivedAuthEvent) setLoading(false); });
    const unsubscribe = onAuthStateChange((session) => {
      receivedAuthEvent = true;
      if (cancelled) return;
      setUser(session?.user ?? null);
      setLoading(false);
    });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  const value = useMemo(() => ({ user, loading }), [user, loading]);
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
